import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import {
  Trophy,
  Star,
  PlusCircle,
  CalendarDays,
  Medal,
  Award,
} from "lucide-react";
import { format } from "date-fns";

const StatsCard = ({ title, value }) => (
  <div className="bg-white rounded-lg p-6 shadow-sm">
    <div className="flex flex-col gap-2">
      <span className="text-gray-600">{title}</span>
      <span className="text-5xl font-bold">{value}</span>
    </div>
  </div>
);

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Codechef = () => {
  const navigate = useNavigate();
  const {
    user: { profile: currentUser },
  } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_CLIENT_FUNCTION_BASE_URL
        }/client_dashboard/codechef/${currentUser.id}`
      );
      const result = await response.json();
      setData(result.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser.id]);

  const refreshUser = async () => {
    try {
      setRefreshing(true);
      const res = await fetch(
        `${
          import.meta.env.VITE_CLIENT_FUNCTION_BASE_URL
        }/codechef/refresh_user/${currentUser.id}`,
        {
          method: "POST",
        }
      );

      if (!res.ok) {
        throw new Error("Error in refreshing user");
      }

      fetchData();
    } catch (error) {
      console.error("Error in refreshing user", error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  if (loading) {
    return (
      <div className="flex flex-col gap-6 w-full max-w-[1200px] mx-auto p-4 animate-pulse">
        {/* Verification Banner Skeleton */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-700/20 to-slate-900/20 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-gray-200"></div>
            <div>
              <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-64 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div className="h-10 w-24 bg-gray-200 rounded"></div>
        </div>

        {/* Refresh Button Skeleton */}
        <div className="flex flex-col gap-1 items-end w-full">
          <div className="h-10 w-32 bg-gray-200 rounded"></div>
          <div className="h-4 w-48 bg-gray-200 rounded"></div>
        </div>

        {/* Main Stats Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex flex-col gap-2">
                <div className="h-4 w-32 bg-gray-200 rounded"></div>
                <div className="h-12 w-24 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Profile Details Card Skeleton */}
        <div className="grid grid-cols-1 gap-6">
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <div className="h-6 w-32 bg-gray-200 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg"
                  >
                    <div className="h-8 w-8 rounded bg-gray-200"></div>
                    <div>
                      <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
                      <div className="h-6 w-16 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-8">
                {[1, 2].map((i) => (
                  <div key={i}>
                    <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Contest History Table Skeleton */}
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <div className="h-6 w-36 bg-gray-200 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left py-3 px-4">
                        <div className="h-4 w-24 bg-gray-200 rounded"></div>
                      </th>
                      <th className="text-center py-3 px-4">
                        <div className="h-4 w-32 bg-gray-200 rounded mx-auto"></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4].map((i) => (
                      <tr key={i} className="border-b last:border-b-0">
                        <td className="py-3 px-4">
                          <div className="h-5 w-48 bg-gray-200 rounded"></div>
                        </td>
                        <td className="text-center py-3 px-4">
                          <div className="h-5 w-16 bg-gray-200 rounded mx-auto"></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="w-full h-full flex items-center justify-center flex-col gap-3">
        <PlusCircle className="h-32 w-32 text-blue-500" />
        <div className="flex flex-col gap-3 items-center justify-center">
          <h1 className="font-semibold text-3xl">Profile is not connected</h1>
          <p>
            Showcase Your Competitive Programming Skills by connecting your
            profile and verify it.
          </p>
          <Button
            onClick={() => navigate("/home/edit/profile/otherprofiles")}
            className="bg-blue-500 text-white"
          >
            Connect
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-[1200px] mx-auto p-4">
      {!data?.verified && (
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-700 to-slate-900 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <Trophy className="h-12 w-12 text-white opacity-80" />
            <div>
              <h2 className="text-xl font-semibold text-white">
                Profile is not verified
              </h2>
              <p className="text-slate-300">
                Verify your profile to showcase your competitive programming
                skills
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/home/edit/profile/otherprofiles")}
            className="bg-white text-black hover:bg-gray-100"
          >
            Verify
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-1 items-items justify-end w-full">
        <Button
          disabled={refreshing}
          onClick={refreshUser}
          className="self-end"
        >
          {refreshing ? "Refreshing..." : "Refresh Data"}
        </Button>
        <span className="self-end text-sm text-gray-500">
          Last Refresh : {format(new Date(data?.lastUpdated), "dd MMMM yyyy")}
        </span>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatsCard
          title="Total Questions"
          value={data?.totalProblemsSolved ?? 0}
        />
        <StatsCard
          title="Contests Participated"
          value={data?.contestsParticipated ?? 0}
        />
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Profile Details Card */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold">
              Profile Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="flex items-center gap-3 bg-orange-50 p-4 rounded-lg">
                <Trophy className="h-8 w-8 text-orange-500" />
                <div>
                  <p className="text-sm text-gray-600">Current Rating</p>
                  <p className="text-xl font-bold">{data?.rating}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-purple-50 p-4 rounded-lg">
                <Award className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="text-sm text-gray-600">Max Rating</p>
                  <p className="text-xl font-bold">{data?.maxRating}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-yellow-50 p-4 rounded-lg">
                <Star className="h-8 w-8 text-yellow-500" />
                <div>
                  <p className="text-sm text-gray-600">Star Rating</p>
                  <p className="text-xl font-bold">{data?.star}★</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-gray-600 mb-1">Username</p>
                <p className="font-semibold">{data?.username}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Full Name</p>
                <p className="font-semibold">{data?.fullName}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contest History Table */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold">
              Contest History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr className="text-sm text-gray-600">
                    <th className="text-left py-3 px-4">Contest</th>
                    <th className="text-center py-3 px-4">Problems Solved</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.contestParticipation?.map((contest) => (
                    <tr
                      key={contest.id}
                      className="border-b last:border-b-0 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">
                            {contest.codechef_contest.name}
                          </p>
                        </div>
                      </td>
                      <td className="text-center py-3 px-4">
                        {contest.problems_solved}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Codechef;
